import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import ddt from '@wix/da-ddt';
import { actions as modalActions } from '../../Modals';
import { selectors, withOffset } from '../../Stream';
import {
  fetchBackgrounds,
  saveBackground,
  saveBackgroundFail,
  saveBackgroundSuccess,
  showBackgroundPicker,
  initializeBackgrounds,
  setBackground,
} from './actions';
import { canUserSetBackground, getCurrentBackgroundId } from './selectors';
import { backgroundsStreamId, streamType } from './constants';
import { BackgroundStreamParams } from './types';
import { doFetchBackgrounds, doSetBackground } from './xhr';
import { ModalType } from '../../Modals/redux/types';
import { PapiDaDeviationBackgrounds, PapiDaDeviationSetBackground } from '@wix/da-papi-types';
import { getIsCurrentUserAuthorOf } from '../../redux/deviations/selectors';
import {
  unloadDuperbrowseItem,
  exitDuperbrowse,
  shutdownDuperbrowse,
} from '../../Duperbrowse/redux/actionCreators';
import { FetchType } from '../../Stream/base/types';

const logger = ddt.createLogger('devbgs');

function* handleStreamFetch({ streamId }: withOffset.types.FetchAction) {
  const streamParams = yield select(selectors.getStreamParams, streamId);
  if (!streamParams) {
    return;
  }

  if (streamParams.type === streamType) {
    yield put(fetchBackgrounds());
  }
}

function* handleFetchBackgrounds(action: ReturnType<typeof fetchBackgrounds>) {
  const { offset, limit } = yield select(state => ({
    offset: withOffset.selectors.getNextOffset(state, backgroundsStreamId),
    limit: selectors.getItemsPerFetch(state, backgroundsStreamId),
  }));

  const result: PapiDaDeviationBackgrounds | false = yield call(doFetchBackgrounds, offset, limit);

  if (!result) {
    logger.log('Failed to fetch backgrounds', result);
    yield put(
      withOffset.actions.fetchFailure({
        streamId: backgroundsStreamId,
        fetchType: FetchType.Next,
        errorMsg: 'Failed to fetch',
      })
    );
    return;
  }

  yield put(
    withOffset.actions.fetchSuccessDeduped({
      streamId: backgroundsStreamId,
      items: result.results,
      hasMore: !!result.hasMore,
      nextOffset: result.nextOffset,
    })
  );
}

function* handleSaveBackground({
  payload: { deviation, backgroundId },
}: ReturnType<typeof saveBackground>) {
  const backendBackgroundId = backgroundId < 0 ? undefined : backgroundId;
  const canSetBackground = yield select(canUserSetBackground, backgroundId);

  if (!canSetBackground) {
    return;
  }

  const originalBackgroundId = yield select(getCurrentBackgroundId, deviation);

  yield put(setBackground(deviation.deviationId, backgroundId));
  const result: PapiDaDeviationSetBackground = yield call(
    doSetBackground,
    deviation.deviationId,
    backendBackgroundId
  );

  if (!result || result.errorDescription) {
    logger.error('Failed to set background', result && result.errorDescription);

    yield all([
      put(saveBackgroundFail(deviation.deviationId, backgroundId)),
      put(setBackground(deviation.deviationId, originalBackgroundId)),
    ]);
    return;
  }

  yield put(saveBackgroundSuccess(deviation.deviationId, backgroundId));
}

function* handleShowPicker({ payload: { deviation } }: ReturnType<typeof showBackgroundPicker>) {
  const isOwner = yield select(getIsCurrentUserAuthorOf, deviation);

  if (!isOwner) {
    return;
  }

  yield all(
    [
      call(handleInitializeBackgrounds),
      put(
        modalActions.pushModal(ModalType.DEVIATION_BACKGROUND_PICKER, {
          wrapInModal: false,
          params: {
            deviation,
          },
        })
      ),
    ].filter(x => x)
  );
}

function* handleUnloadDuper() {
  yield put(modalActions.removeModalsOfType(ModalType.DEVIATION_BACKGROUND_PICKER));
}

function* handleInitializeBackgrounds() {
  const existingStreamParams = yield select(selectors.getStreamParams, backgroundsStreamId);
  if (existingStreamParams && existingStreamParams.type === streamType) {
    return;
  }

  const streamParams: BackgroundStreamParams = {
    type: streamType,
  };

  yield put(
    withOffset.actions.initialize({
      streamId: backgroundsStreamId,
      itemsPerFetch: 24,
      items: [],
      hasMore: true,
      streamParams,
    })
  );
}

export default function* backgroundsRootSaga() {
  yield all([
    takeEvery(initializeBackgrounds, handleInitializeBackgrounds),
    takeEvery(withOffset.actions.STREAM__WITH_OFFSET__FETCH, handleStreamFetch),
    takeLatest(fetchBackgrounds, handleFetchBackgrounds),
    takeEvery(saveBackground, handleSaveBackground),
    takeEvery(showBackgroundPicker, handleShowPicker),
    takeEvery([unloadDuperbrowseItem, exitDuperbrowse, shutdownDuperbrowse], handleUnloadDuper),
  ]);
}
